import { FC, useCallback, useMemo } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useImmerReducer } from 'use-immer';

import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { useUploadApprovalDocument } from 'src/use-cases/invoice/useInvoices';
import { createAxiosInstance } from 'src/utils/createAxiosInstance';
import { attachCommonErrorInterceptor } from 'src/infra/services/interceptors';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from 'src/utils/date';
import { UploadInputRaw } from 'src/presentations/components/atom/upload';
import { ACCEPT_FILE } from 'src/config';
import { UCTInputUploadProps } from './types';
import { UCTCardFile } from './CardFile';

const axios = createAxiosInstance({}, [attachCommonErrorInterceptor]);

const initialState: UploadState = {
  fileUpload: null,
  uploadError: '',
  uploadPercentage: 0,
  isShowDeleteConfirm: false,
  isShowCancelConfirm: false,
};

interface UploadState {
  uploadPercentage: number;
  // fileUpload will be non-null while uploading
  fileUpload: FileProps;
  uploadError: string;
  isShowDeleteConfirm: boolean;
  isShowCancelConfirm: boolean;
}

interface FileProps {
  name: string;
  date: string;
}

type UploadAction =
  | {
      type: 'uploading';
      payload: number;
    }
  | {
      type: 'showUploadingFile';
      payload: FileProps;
    }
  | {
      type: 'error';
      payload: string;
    }
  | {
      type: 'deleting';
    }
  | {
      type: 'deleteSuccess';
    }
  | {
      type: 'openDeleteConfirm';
    }
  | {
      type: 'closeDeleteConfirm';
    }
  | {
      type: 'openCancelConfirm';
    }
  | {
      type: 'closeCancelConfirm';
    }
  | {
      type: 'uploadComplete';
    };

const uploadReducer = (state: UploadState, action: UploadAction): UploadState => {
  switch (action.type) {
    case 'uploading':
      state.uploadPercentage = action.payload;
      return state;
    case 'showUploadingFile':
      state.uploadPercentage = 0;
      state.fileUpload = action.payload;
      state.uploadError = '';
      return state;
    case 'uploadComplete':
      state.fileUpload = null;
      return state;
    case 'deleting':
      state.fileUpload = null;
      state.isShowDeleteConfirm = false;
      return state;

    case 'error':
      state.fileUpload = null;
      state.uploadError = action.payload;
      return state;
    case 'openDeleteConfirm':
      state.isShowDeleteConfirm = true;
      return state;
    case 'closeDeleteConfirm':
      state.isShowDeleteConfirm = false;
      return state;
    case 'openCancelConfirm':
      state.isShowCancelConfirm = true;
      return state;
    case 'closeCancelConfirm':
      state.isShowCancelConfirm = false;
      return state;

    default:
      return state;
  }
};

export const UCTInputUploadDocument: FC<UCTInputUploadProps> = (props) => {
  const { invoiceSummary, attachmentType, isPreview, disabled, showAllType } = props;
  const [state, dispatch] = useImmerReducer(uploadReducer, initialState);
  const { uploadError, uploadPercentage, fileUpload, isShowCancelConfirm } = state;

  const queryClient = useQueryClient();

  const { mutateAsync: getUploadApprovalDoc } = useUploadApprovalDocument();

  /**
   * uploadFile
   */
  const uploadFile = useCallback(
    async (uploadUrl: string, file: File) => {
      await axios.put(uploadUrl, file, {
        onUploadProgress: (progressEvent) => {
          const percentage = Math.floor(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          dispatch({ type: 'uploading', payload: percentage });
        },
        method: 'PUT',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    [dispatch]
  );

  /**
   * uploadDocument
   */
  const uploadDocument = useCallback(
    async (file: File) => {
      dispatch({ type: 'showUploadingFile', payload: { name: file.name, date: null } });
      const uploadUrl = await getUploadApprovalDoc({ invoiceId: invoiceSummary.id, fileName: encodeURI(file.name), attachmentType });

      await uploadFile(uploadUrl, file);

      queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary.id));
      queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary.id));

      dispatch({ type: 'uploadComplete' });
    },
    [dispatch, getUploadApprovalDoc, invoiceSummary.id, attachmentType, uploadFile, queryClient]
  );

  /**
   * onDrop
   */
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        uploadDocument(file);
        dispatch({ type: 'showUploadingFile', payload: { name: file.name, date: null } });
      } else if (rejectedFiles.length > 0) {
        rejectedFiles.forEach((file) => {
          file.errors.forEach(() => {
            // dispatch({ type: 'error', payload: err.message });
          });
        });
      }
    },
    [dispatch, uploadDocument]
  );

  const dropzone = useDropzone({
    onDrop,
    multiple: false,
    accept: ACCEPT_FILE,
    disabled,
  });

  /**
   * onDeleteFile
   */
  const onDeleteFile = async () => {
    dispatch({ type: 'deleting' });
  };

  /**
   * showCancelConfirm
   */
  const showCancelConfirm = () => {
    dispatch({ type: 'openCancelConfirm' });
  };

  const attachments = useMemo(() => {
    if (showAllType) {
      return invoiceSummary?.invoiceAttachments;
    }
    return invoiceSummary?.invoiceAttachments?.filter((attachment) => attachment?.attachmentType === attachmentType);
  }, [attachmentType, invoiceSummary, showAllType]);

  return (
    <>
      <div className='mt-24'>
        {!isPreview && <UploadInputRaw disabled={disabled} dropzone={dropzone} error={<p className='text-14 text-red-40'>{uploadError}</p>} />}

        {fileUpload && <UCTCardFile attachment={null} name={fileUpload?.name} uploadPercentage={uploadPercentage} onCancelUpload={showCancelConfirm} />}

        {attachments?.length > 0 && (
          <>
            {attachments?.map((attachment) => (
              <UCTCardFile
                key={attachment.id}
                attachment={attachment}
                name={attachment?.fileName}
                uploadPercentage={100}
                dateInfo={formatDate(new Date(attachment?.createdOn))}
                showDeleteButton={!isPreview && !disabled}
              />
            ))}
          </>
        )}

        {attachments?.length < 1 && isPreview && <p className='text-neutral-60'>No document uploaded</p>}
      </div>

      <ModalConfirm
        open={isShowCancelConfirm}
        title='Warning'
        onAccept={onDeleteFile}
        onCancel={() => dispatch({ type: 'closeCancelConfirm' })}
        buttonAcceptLabel='Discard'
      >
        <p>The uploaded document will not be saved. Are you sure want to discard your changes?</p>
      </ModalConfirm>
    </>
  );
};
